import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Maybe, SearchResultPageInfo } from '@/types/graphql.prod';
import { usePathname, useSearchParams } from 'next/navigation';

export const ContentPagination = ({ pageInfo }: { pageInfo: Maybe<SearchResultPageInfo> | undefined }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryStr = searchParams.toString();
  const pathnamePartsWithoutPagination = pathname.split('/').filter((x, i, arr) => {
    return !(!x || x === 'p' || (i > 0 && arr[i - 1] === 'p' && /^\d+$/.test(x)));
  });

  if (!pageInfo || !pageInfo.current_page || !pageInfo.total_pages) {
    return null;
  }

  const currentPage = Number(pageInfo.current_page);
  const totalPages = Number(pageInfo.total_pages);

  if (totalPages === 1) {
    return null;
  }

  // Generate array of page numbers to show
  const getPageNumbers = () => {
    const pages: number[] = [];
    const showMax = 5; // Maximum number of page numbers to show

    if (totalPages <= showMax) {
      // If total pages is less than or equal to showMax, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always include first page
      pages.push(1);

      // Calculate start and end of middle section
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Always include last page
      pages.push(totalPages);
    }

    return pages;
  };

  const getPageUrl = (pageNum: number) => {
    if (pageNum === 1) {
      return '/' + pathnamePartsWithoutPagination.join('/') + (queryStr ? `?${queryStr}` : '');
    }

    return '/' + pathnamePartsWithoutPagination.join('/') + `/p/${pageNum}` + (queryStr ? `?${queryStr}` : '');
  };

  return (
    <Pagination className='mt-[50px]'>
      <PaginationContent>
        {currentPage > 1 ? (
          <PaginationItem>
            <PaginationPrevious
              href={getPageUrl(currentPage > 1 ? currentPage - 1 : 1)}
              className={currentPage <= 1 ? 'pointer-events-none opacity-50' : ''}
            />
          </PaginationItem>
        ) : null}

        {getPageNumbers().map((pageNum, index) =>
          // eslint-disable-next-line implicit-arrow-linebreak
          pageNum === -1 ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={pageNum}>
              <PaginationLink
                href={getPageUrl(pageNum)}
                isActive={pageNum === currentPage}
              >
                {pageNum}
              </PaginationLink>
            </PaginationItem>
          ),
        )}

        {currentPage < totalPages ? (
          <PaginationItem>
            <PaginationNext
              href={getPageUrl(currentPage < totalPages ? currentPage + 1 : totalPages)}
              className={currentPage >= totalPages ? 'pointer-events-none opacity-50' : ''}
            />
          </PaginationItem>
        ) : null}
      </PaginationContent>
    </Pagination>
  );
};
